import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Toast as ToastType, useToastStore } from '@/common/stores/useToastStore';

const toastStyles: Record<
  ToastType['type'],
  { bg: string; text: string; icon: typeof CheckCircleIcon }
> = {
  success: {
    bg: 'bg-green-50 border-green-200',
    text: 'text-green-800',
    icon: CheckCircleIcon,
  },
  danger: {
    bg: 'bg-red-50 border-red-200',
    text: 'text-red-800',
    icon: XCircleIcon,
  },
  warning: {
    bg: 'bg-yellow-50 border-yellow-200',
    text: 'text-yellow-800',
    icon: ExclamationCircleIcon,
  },
  info: {
    bg: 'bg-blue-50 border-blue-200',
    text: 'text-blue-800',
    icon: InformationCircleIcon,
  },
};

const Toast = ({ toast }: { toast: ToastType }) => {
  const { t } = useTranslation();
  const removeToast = useToastStore((state) => state.removeToast);
  const toastRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (toastRef.current) {
      toastRef.current.style.opacity = '0';
      toastRef.current.style.transform = 'translateX(100%)';

      requestAnimationFrame(() => {
        if (toastRef.current) {
          toastRef.current.style.opacity = '1';
          toastRef.current.style.transform = 'translateX(0)';
        }
      });
    }

    const duration = toast.duration * 1000;
    const fadeOutDuration = 300;

    timeoutRef.current = setTimeout(() => {
      if (toastRef.current) {
        toastRef.current.style.opacity = '0';
        toastRef.current.style.transform = 'translateX(100%)';
      }

      setTimeout(() => {
        removeToast(toast.id);
      }, fadeOutDuration);
    }, duration - fadeOutDuration);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [toast.duration, toast.id, removeToast]);

  const handleClose = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (toastRef.current) {
      toastRef.current.style.opacity = '0';
      toastRef.current.style.transform = 'translateX(100%)';

      setTimeout(() => {
        removeToast(toast.id);
      }, 300);
    }
  };

  const style = toastStyles[toast.type];
  const Icon = style.icon;

  return (
    <div
      ref={toastRef}
      className={`
        flex items-start gap-3 p-4 rounded-lg border shadow-lg
        min-w-[320px] max-w-md
        transition-all duration-300 ease-in-out
        ${style.bg} ${style.text}
      `}
      role="alert"
    >
      <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        {toast.title && <div className="font-semibold text-sm mb-1">{toast.title}</div>}
        <div className="text-sm">{toast.message}</div>
      </div>
      <button
        aria-label={t('toast.close')}
        className="flex-shrink-0 p-1 rounded hover:bg-black/10 transition-colors"
        onClick={handleClose}
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

export const ToastContainer = () => {
  const toasts = useToastStore((state) => state.toasts);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} />
      ))}
    </div>
  );
};
